<template>
  <Dialog
    v-model="dialog"
    :title="$t('vitalSignsAgreement.title')"
    header-type="information"
    left-header-icon="icons/vital-signs.svg"
  >
    <template #text>
      <i18n path="vitalSignsAgreement.perex" tag="p" class="mb-4 text-left">
        <template #termsLink>
          <a
            :href="$localePath({ name: 'termsAndConditionsAi' })"
            target="_blank"
            class="link--inline primary--text"
          >
            {{ $t('vitalSignsAgreement.termsLinkText') }}
          </a>
        </template>
      </i18n>

      <i18n path="vitalSignsAgreement.dataProcessing" tag="p" class="mb-6 text-left">
        <template #privacyLink>
          <a
            :href="$localePath({ name: 'personalDataProcessing' })"
            target="_blank"
            class="link--inline primary--text"
          >
            {{ $t('vitalSignsAgreement.privacyLinkText') }}
          </a>
        </template>
      </i18n>

      <v-checkbox
        v-model="isAgreed"
        class="vital-signs-agreement__checkbox mt-0 pt-0"
        hide-details
        data-cy="vitalSignsAgreementCheckbox"
      >
        <template #label>
          <i18n path="vitalSignsAgreement.agreeCheckbox" tag="span">
            <template #termsLink>
              <a
                :href="$localePath({ name: 'termsAndConditionsAi' })"
                target="_blank"
                class="link--inline primary--text"
                @click.stop
              >
                {{ $t('vitalSignsAgreement.termsLinkText') }}
              </a>
            </template>
          </i18n>
        </template>
      </v-checkbox>
    </template>

    <template #actions>
      <div class="pt-4 pb-4 pb-md-10 px-4 px-md-10 flex-grow-1">
        <SendButton
          :loading="loading"
          :disabled="!isAgreed"
          block
          :button-text="$t('vitalSignsAgreement.confirmButton')"
          data-cy="vitalSignsAgreementConfirm"
          @click="onAgree"
        />
      </div>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { computed, getCurrentInstance, ref, watch, type ComponentPublicInstance } from 'vue';
import Dialog from '@/components/Atoms/Dialog.vue';
import SendButton from '@/components/Forms/Parts/SendButton.vue';
import {
  VITAL_SIGNS_DIALOG_KEY,
  VITAL_SIGNS_CATEGORY,
  VITAL_SIGNS_TYPE_NAME
} from '@/feature/vital-signs/composables/useVitalSignsConsent';

const GTM_NAME = 'vital_signs_consent';
const ROUTE_NAME = 'vitalSigns';

type LegalDocument = {
  id: number | string;
  category: string;
  typeName: string;
  languageCode: string;
  regionCode: string;
  version: number;
};

type StoreWithHttp = {
  state: { profile: { dashboard: { dialog: string | false } } };
  getters: Record<string, unknown>;
  commit: CallableFunction;
  dispatch: CallableFunction;
  $http: { get: CallableFunction; post: CallableFunction };
};

type VmProxy = ComponentPublicInstance & {
  $store: StoreWithHttp;
  $i18n: { locale: string };
  $router: { push: CallableFunction };
  $domainRegion: 'cz' | 'sk' | 'hu' | undefined;
  $localePath: CallableFunction;
  $auth: { fetchUser: CallableFunction };
  gtmPush?: CallableFunction;
};

const vm = getCurrentInstance()?.proxy as unknown as VmProxy;
const loading = ref(false);
const isAgreed = ref(false);

const dialog = computed<boolean>({
  get: () => vm.$store.state.profile.dashboard.dialog === VITAL_SIGNS_DIALOG_KEY,
  set: value =>
    vm.$store.commit('profile/dashboard/setDialog', value ? VITAL_SIGNS_DIALOG_KEY : false)
});

watch(dialog, newValue => {
  vm.gtmPush?.({
    event: newValue ? 'notification_show' : 'notification_close',
    'notification.type': GTM_NAME
  });
  if (!newValue) {
    isAgreed.value = false;
  }
});

function pickLatest(docs: LegalDocument[]): LegalDocument | undefined {
  const locale = vm.$i18n.locale;
  const region = vm.$domainRegion;
  const candidates = docs.filter(
    doc =>
      doc.category === VITAL_SIGNS_CATEGORY &&
      doc.typeName === VITAL_SIGNS_TYPE_NAME &&
      doc.languageCode.toLowerCase() === locale &&
      doc.regionCode.toLowerCase() === region
  );
  if (!candidates.length) {
    return undefined;
  }
  return candidates.reduce((best, curr) => (curr.version > best.version ? curr : best));
}

async function resolveDocumentId(): Promise<LegalDocument['id'] | null> {
  const byCategory = vm.$store.getters['legalDocuments/legalDocuments'] as Record<
    string,
    LegalDocument[] | undefined
  >;
  const cached = byCategory[VITAL_SIGNS_CATEGORY];
  const cachedMatch = cached ? pickLatest(cached) : undefined;
  if (cachedMatch) {
    return cachedMatch.id;
  }

  vm.$store.dispatch('legalDocuments/setLoading', true);
  try {
    const data = (await vm.$store.$http.get(
      `/v3/legal-documents?category=${VITAL_SIGNS_CATEGORY}&languageCode=${vm.$i18n.locale}`
    )) as LegalDocument[];
    vm.$store.dispatch('legalDocuments/setLegalDocuments', {
      category: VITAL_SIGNS_CATEGORY,
      data
    });
    return pickLatest(data)?.id ?? null;
  } catch (error) {
    vm.$store.dispatch('errorModal/handleError', { error, showModal: false, code: '0x00000060' });
    return null;
  } finally {
    vm.$store.dispatch('legalDocuments/setLoading', false);
  }
}

async function onAgree() {
  if (!isAgreed.value) {
    return;
  }
  loading.value = true;

  const documentId = await resolveDocumentId();
  if (!documentId) {
    loading.value = false;
    return;
  }

  try {
    await vm.$store.$http.post(`/v3/legal-documents/${documentId}/confirm`);
    await vm.$auth.fetchUser();
    // Push first, then close the dialog — otherwise profileDialogs watcher
    // races with our redirect and the user ends up back on the dashboard.
    await vm.$router.push(vm.$localePath(ROUTE_NAME)).catch(() => {});
    dialog.value = false;
  } catch (error) {
    vm.$store.dispatch('errorModal/handleError', {
      error,
      showModal: false,
      code: '0x00000022'
    });
    dialog.value = false;
  } finally {
    loading.value = false;
  }
}
</script>

<style lang="scss" scoped>
.vital-signs-agreement__checkbox {
  ::v-deep .v-label {
    color: inherit;
  }
}
</style>
